'use client';

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { logClientEvent } from '@/lib/log-client-event';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LocaleLink } from '@/components/locale-link';
import {
  CREDIT_TOPUP_CREDITS_PER_UNIT,
  CREDIT_TOPUP_MIN_QUANTITY,
} from '@/config/stripe-plans.config';
import { loggedToast } from '@/lib/logged-toast';
import { createTopupCheckoutSession } from '@/lib/stripe-client';

/**
 * Centered modal shown when an action fails because the user's credit
 * balance is used up. The main button goes straight to Stripe Checkout for
 * the minimum top-up (5 € / 500 credits, adjustable on the Stripe page);
 * backing out of Stripe returns to the page the user was on. The pricing
 * page stays one click away for upgrading instead. Shared by every
 * insufficient-credits (402) surface — controlled, so each caller owns
 * its own `open` state.
 */
export function InsufficientCreditsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    if (open) logClientEvent('paywall_shown', undefined, { kind: 'credits', feature: 'credits' });
  }, [open]);

  // Coming back from Stripe via the browser Back button restores the page
  // from the bfcache with the spinner still running — reset it.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRedirecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleTopup = async () => {
    setRedirecting(true);
    try {
      await createTopupCheckoutSession(
        CREDIT_TOPUP_MIN_QUANTITY,
        window.location.pathname + window.location.search,
      );
      // Redirecting to Stripe — keep the spinner.
    } catch {
      loggedToast.error(t('pages.plans.checkoutError'));
      setRedirecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('credits.outOfCreditsDialog.title', 'Keine Credits mehr')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'credits.outOfCreditsDialog.description',
              'Ihr Guthaben ist aufgebraucht. Laden Sie Credits auf (1 € / 100 Credits) oder wechseln Sie zu einem größeren Plan, um fortzufahren.',
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" asChild>
            <LocaleLink href="/plans" onClick={() => onOpenChange(false)}>
              {t('credits.outOfCreditsDialog.seePlans', 'Tarife ansehen')}
            </LocaleLink>
          </Button>
          <Button onClick={handleTopup} disabled={redirecting}>
            {redirecting && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {t('credits.outOfCreditsDialog.cta', {
              defaultValue: '{{euros}} € aufladen ({{credits}} Credits)',
              euros: CREDIT_TOPUP_MIN_QUANTITY,
              credits: CREDIT_TOPUP_MIN_QUANTITY * CREDIT_TOPUP_CREDITS_PER_UNIT,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
